// Customer-facing kcal formatting for order-react. "kcal" is a universal unit
// symbol so it's hardcoded (the Kioskv3 design does the same). Every show/hide
// rule the API contract implies lives here, so the components stay dumb.

const UNIT = 'kcal';

export const formatKcal = (n) => `${n} ${UNIT}`;

// Menu-card number. Only items with a real, visible calorie count
// (`calories != null && showcalories !== false`). One value (or all-equal) → a
// single number; distinct → a min–max range with an en-dash; none → null.
export const productKcalLabel = (items) => {
  const vals = (items || [])
    .filter((it) => it.calories != null && it.showcalories !== false)
    .map((it) => it.calories);
  if (vals.length === 0) return null;
  const lo = Math.min(...vals);
  const hi = Math.max(...vals);
  return lo === hi ? formatKcal(lo) : `${lo}–${hi} ${UNIT}`; // en-dash U+2013
};

// One main-option chip's kcal, honoring showcalories. A 0-kcal item shows
// "0 kcal"; a hidden or null item shows nothing.
export const mainOptionKcalLabel = (item) => {
  if (!item || item.showcalories === false || item.calories == null) return null;
  return formatKcal(item.calories);
};

// One modifier chip's kcal. Add (mode 0) → signed delta; Replace (mode 1) →
// absolute with a leading '=' to signal it overrides the main option's calories.
// null or 0 → nothing.
export const modifierKcalLabel = (calories, caloriesmode) => {
  if (calories == null || calories === 0) return null;
  if (Number(caloriesmode) === 1) return `=${calories} ${UNIT}`;
  return `${calories > 0 ? '+' : ''}${calories} ${UNIT}`;
};

// The kcal of a concrete selection: the chosen main option plus its chosen
// modifiers. Null when the item hides or lacks calories — distinct from 0,
// which is a real, displayable value.
//
// Two independent accumulators, mirroring customerapp exactly: `base` starts at
// the item's calories and is overwritten by each Replace (the LAST one in list
// order wins); `added` sums every Add regardless of where it sits relative to a
// Replace, so an Add selected first is not discarded. Options with null or 0
// calories are skipped BEFORE the mode is inspected — they render nothing, and
// a 0-calorie Replace must not zero the base nor displace an earlier one. The
// total is clamped once, at the end. It is never multiplied by qty.
export const selectionKcal = (item, selectedOptions) => {
  if (!item || item.showcalories === false || item.calories == null) return null;
  let base = item.calories;
  let added = 0;
  for (const option of selectedOptions || []) {
    const calories = option?.calories;
    if (calories == null || calories === 0) continue;
    if (Number(option.caloriesmode) === 1) {
      base = calories;
    } else {
      added += calories;
    }
  }
  const total = base + added;
  return total < 0 ? 0 : total;
};

// selectionKcal as a display label ("570 kcal"), or null when it has nothing.
export const selectionKcalLabel = (item, selectedOptions) => {
  const total = selectionKcal(item, selectedOptions);
  return total == null ? null : formatKcal(total);
};

// Formik stores modifiers as one array of option ids PER GROUP, keyed by group
// id (`values['<groupId>'] = ['<optionId>', ...]`). Resolve them back to the
// real option objects, which carry calories/caloriesmode — the cart's persisted
// options do not (getPrices strips them).
//
// This helper walks the groups in whatever order it is given — it does not sort.
// The caller (ProductForm) passes them pre-sorted by displayorder, i.e. the
// order the user actually sees on screen, which is the closest available proxy
// to true tap order across groups.
//
// Within a group the array is in tap order, so it is honoured. ACROSS groups
// there is still no global tap order and Formik holds nothing to reconstruct
// one, so two Replace modifiers in different groups can still resolve
// differently here than in the Flutter apps, where the selection is one flat
// list in genuine tap order.
export const resolveSelectedOptions = (values, optionGroups) => {
  const resolved = [];
  for (const group of optionGroups || []) {
    const ids = values?.[group.id] || [];
    for (const id of ids) {
      const found = (group.options || []).find((o) => o.id === id);
      if (found) resolved.push(found);
    }
  }
  return resolved;
};
